<template>
  <b-container fluid>
    <b-row align-v="bottom">
      <b-col md="12" lg="9">
        <div class="reports__sample-container">
          <div class="text-center text-lg-left mb-0 reports__sample-info">
            <div class="d-inline-block mr-3">
              <strong>
                {{ $t('engineering.reports.sample') }}:
              </strong>
              <span>
                {{ sampleNumber }}
              </span>
            </div>
            <div class="d-inline-block mr-0 mr-lg-3">
              <strong>
                {{ $t('engineering.reports.sample-type') }}:
              </strong>
              {{ sampleType }}
            </div>
            <div class="reports__buttons d-block d-lg-inline-block text-center text-lg-left">
              <b-button
                class="mr-3 mt-3"
                size="sm"
                variant="outline-secondary"
                @click="confirmEditSample"
              >
                <span class="fa fa-edit" />
                {{ $t('actions.edit') }}
              </b-button>
              <b-button
                class="mt-3"
                size="sm"
                variant="outline-secondary"
                @click="openPrintPage"
              >
                <s-icon
                  name="print"
                  class="mr-1"
                />
                {{ $t('actions.print') }}
              </b-button>
            </div>
          </div>
        </div>
      </b-col>
      <b-col
        md="12"
        lg="3"
        class="reports__toggle-container pt-3 px-0 text-center text-lg-right">
        <b-form-group>
          <b-form-radio-group
            buttons
            button-variant="outline-primary"
            class="reports__view-toggle"
            size="sm"
            v-model="selected"
            :options="reportTypes"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="p-0">
        <summary-transformer-info
          class="mb-3 mt-2"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-form-checkbox
        v-model="showAlarmedOnly"
      >{{ $t('engineering.reports.alarmed-methods') }}</b-form-checkbox>
    </b-row>
    <b-row v-if="showData" class="reports__table mt-3">
      <b-col cols="12" class="p-0">
        <flex-table
          show-values
          :table="dataToShow"
          :tests-fields="testsFields"
          :is-parameters="isParameterSelected"
        />
      </b-col>
    </b-row>
    <b-row v-else align-h="center">
      <s-empty-list
        :loading="loading"
      />
    </b-row>

  </b-container>
</template>

<script>
import compact from 'lodash/compact';
import pick from 'lodash/pick';
import FlexTable from '@/domain/components/installation/engineering/physicalChemical/FlexTable';
import Service from '@/domain/services/installation/engineering/physicalChemical/generalTab';
import { sampleType } from '@/domain/enums/sample';
import SummaryTransformerInfo from '@/domain/components/installation/supervision/SummaryTransformerInfo';
import TestsFields from '@/domain/data/installation/engineering/physicalChemical/reports/lots-and-parameters-tests-fields.json';

export default {
  name: 'SampleView',
  props: {
    sampleId: String,
    assetModuleId: String,
  },
  components: {
    FlexTable,
    SummaryTransformerInfo,
  },
  data: () => ({
    sampleNumber: null,
    sampleType: null,
    parameters: null,
    reports: null,
    selected: 'reports',
    loading: false,
    notProcessed: false,
    showAlarmedOnly: false,
  }),
  computed: {
    dataToShow() {
      const dataToShow = this.selected === 'reports'
        ? this.reports
        : this.parameters;

      if (!this.showAlarmedOnly) {
        return dataToShow;
      }
      const testKeys = Object.entries(this.filterTests(dataToShow)).map(test => test[0]);
      const alarmedRules = dataToShow.byRules
        .filter(({ alarmedFields }) => alarmedFields.length);

      if (this.selected === 'reports') {
        testKeys.push('isAlarmed');
        return {
          ...dataToShow,
          byRules: [...alarmedRules.map(it => ({ ...it, rules: [pick(it.rules[0], testKeys)] }))],
          tests: this.filterTests(dataToShow),
        };
      }
      return {
        ...dataToShow,
        byRules: alarmedRules,
      };
    },
    testsFields() {
      return this.selected === 'reports'
        ? this.reportFieldsToShow
        : this.parametersFieldsToShow;
    },
    reportFieldsToShow() {
      if (!this.showAlarmedOnly) {
        return TestsFields.lotsTestsFields;
      }

      return TestsFields.lotsTestsFields.filter(field => this.parameters
        .byRules.some(({ alarmedFields }) => alarmedFields.includes(field.key)));
    },
    parametersFieldsToShow() {
      if (!this.showAlarmedOnly) {
        return TestsFields.parametersTestsFields;
      }

      return TestsFields.parametersTestsFields.filter(field => this.parameters
        .byRules.some(({ alarmedFields }) => alarmedFields.includes(field.key)));
    },
    isParameterSelected() {
      return this.selected === 'parameters';
    },
    reportTypes() {
      return [
        {
          text: this.$t('engineering.reports.label'),
          value: 'reports',
        },
        {
          text: this.$t('general.parameters'),
          value: 'parameters',
        },
      ];
    },
    hasReportResults() {
      return this.dataToShow
        && this.dataToShow.byRules
        && this.dataToShow.byRules.some(norm => norm.rules && norm.rules.length > 0);
    },
    showData() {
      return !this.loading && this.hasReportResults;
    },
  },
  methods: {
    filterTests(dataToShow) {
      const testKeys = Object.keys(dataToShow.tests);
      const alarmedKeys = testKeys.map((key) => {
        const valuesFromRules = [];
        dataToShow.byRules.forEach((item) => {
          item.rules.forEach((rule) => {
            if (rule[key]) {
              valuesFromRules.push(rule[key].description);
            }
          });
        });
        return valuesFromRules.find(item => item && item !== 'OK') ? key : undefined;
      });
      const alarmedKeysWithoutFalsy = compact(alarmedKeys);
      const arrayWithTestsAndValues = Object.entries(dataToShow.tests);
      const filteredKeys = arrayWithTestsAndValues
        .filter(test => alarmedKeysWithoutFalsy.includes(test[0]));
      return Object.fromEntries(filteredKeys);
    },
    confirmEditSample() {
      this.$confirmMessage({ code: 'MC036' }).accepted(() => {
        this.editSample();
      });
    },
    editSample() {
      const routeData = this.$router.resolve({
        name: 'monitoring.engineering.module',
        params: this.$route.params,
        query: {
          tab: 'sample',
          sampleId: this.sampleId,
        },
      });
      window.open(routeData.href, '_blank');
    },
    getTableItems() {
      this.loading = true;
      Service.getSampleTables(this.assetModuleId, this.sampleId)
        .then((response) => {
          this.parameters = response.parameters;
          this.reports = response.reports;
          this.sampleType = this.getSampleType(response.reports);
          this.sampleNumber = response.sampleNumber || '--';
          this.notProcessed = response.processingPending;
          if (this.notProcessed) {
            this.$pushAlert({
              message: 'engineering.reports.processing-pending-sample',
              type: 'warning',
              title: 'general.attention',
              autoDismissible: false,
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getSampleType(reports = {}) {
      if (!reports.tests) {
        return '';
      }
      const sampleTypeLabel = {
        [sampleType.USED]: this.$t('enums.sample-type.used'),
        [sampleType.NEW]: this.$t('enums.sample-type.new'),
        [sampleType.RECONDICIONED]: this.$t('enums.sample-type.recondicioned'),
        [sampleType.REGENERATED]: this.$t('enums.sample-type.regenerated'),
      };
      return sampleTypeLabel[reports.tests.sampleType];
    },
    openPrintPage() {
      const routeData = this.$router.resolve({
        name: 'clean-view.physical-chemical-lots-and-parameters.print',
        params: {
          ...this.$route.params,
          sampleId: this.sampleId,
        },
        query: {
          alarmedOnly: this.showAlarmedOnly,
          parameters: this.isParameterSelected,
        },
      });
      window.open(routeData.href, '_blank');
    },
  },
  created() {
    this.getTableItems();
  },
};
</script>

<style lang="sass" scoped>
@import "../../../../../../styles/defaults/mobile-first"

.reports
  &__buttons
    vertical-align: bottom
  &__sample-info
    font-size: 1.4em
  &__table
    margin-bottom: 1rem
</style>
